import { ROUTES, RouteName, type SignUpSource } from '@/constants';
import { useGeo, useGlobalState } from '@/hooks';
import { useAuth } from '@/hooks/useAuth.hook';
import { type IUserInfo, LoginMethod } from '@/interfaces';
import { PmtOnboardingPopupCta } from '@/interfaces/tracking/trackingOnboardingPopup.interfaces';
import { getGaClientId, getSessionId } from '@/modules/pmt/utils/ga4.utils';
import { userInfoModify } from '@/utils';
import { useMemo, useState } from 'react';

type HandleSuccess = (code: string) => void;

type UseLinkedInState = (args: {
  shouldReloadAfterSuccess?: boolean;
  customHandleSuccess?: (userInfo: IUserInfo) => void;
  origin: string;
  signUpSource: SignUpSource;
  newsletterOptIn: boolean;
}) => {
  isLoading: boolean;
  handleSuccess: HandleSuccess;
  redirectUri: string;
};

const useLinkedInState: UseLinkedInState = ({
  shouldReloadAfterSuccess = true,
  customHandleSuccess,
  origin,
  signUpSource,
  newsletterOptIn,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { geoInfo } = useGeo();
  const { isMultiLanguagePage, isFromGetNotifiedBtn, isFromEmailSyncBtn, isFromDelayedShipmentBtn } = useGlobalState();

  const { authPM } = useAuth();

  const redirectUri = `${origin}${ROUTES[RouteName.SignInWithLinkedIn].path}`;

  const cta = useMemo(() => {
    switch (true) {
      case isFromGetNotifiedBtn:
        return PmtOnboardingPopupCta.Noti;
      case isFromEmailSyncBtn:
        return PmtOnboardingPopupCta.Login;
      case isFromDelayedShipmentBtn:
        return PmtOnboardingPopupCta.DelayedShipment;
      default:
    }
  }, [isFromGetNotifiedBtn, isFromEmailSyncBtn, isFromDelayedShipmentBtn]);

  const handleSuccess: HandleSuccess = async (code) => {
    setIsLoading(true);

    try {
      const response = await authPM({
        mediumOrigin: 'linkedin',
        code,
        redirectUri,
        country: geoInfo.country,
        signUpSource,
        newsletterOptIn,
        gaClientId: getGaClientId(),
        gaSessionId: getSessionId(isMultiLanguagePage),
        eventTimestamp: Date.now.toString(),
        cta,
        authMethod: LoginMethod.LinkedIn,
      });

      const modifiedUserInfo = userInfoModify(response.user_info);

      if (shouldReloadAfterSuccess) {
        location.reload();
      }

      customHandleSuccess?.(modifiedUserInfo); // Change to optional chain: biome rule
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleSuccess,
    redirectUri,
  };
};

export default useLinkedInState;
